import React from 'react';
import { 
  Solution, 
  SolutionListItem, 
  SolutionTier, 
  Challenge, 
  Sector, 
  ImplementationStep
} from '../types/solution.types';
import { 
  ChartBarIcon, 
  ChatIcon, 
  UserGroupIcon, 
  ShoppingCartIcon, 
  MailIcon, 
  DocumentTextIcon, 
  CogIcon,
  UserIcon
} from '@heroicons/react/outline';

const API_BASE_URL = '';

const iconMap: { [key: string]: React.ComponentType<any> } = {
  ChartBarIcon,
  ChatIcon,
  UserGroupIcon,
  ShoppingCartIcon,
  MailIcon,
  DocumentTextIcon,
  CogIcon,
  UserIcon
};

const getIconComponent = (iconName?: string): React.ReactNode => {
  if (!iconName) return null;
  const IconComponent = iconMap[iconName];
  return IconComponent ? React.createElement(IconComponent, { className: 'h-6 w-6' }) : null;
};

const getImplementationIcon = (iconName?: string): React.ReactNode => {
  if (!iconName) return null;
  const IconComponent = iconMap[iconName];
  return IconComponent ? React.createElement(IconComponent, { className: 'h-5 w-5' }) : null;
}

export const getAllSolutions = async (): Promise<SolutionListItem[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/solutions`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const responseData = await response.json();
    
    const allSolutions: Solution[] = responseData.data;

    if (!Array.isArray(allSolutions)) {
      console.error("API response did not contain an array in the 'data' property:", responseData);
      return [];
    }

    return allSolutions.map(sol => ({
      id: sol.id,
      title: sol.title,
      description: sol.tagline,
      icon: getIconComponent(sol.icon),
      tier: sol.tier,
      challenges: sol.challenges,
      sectors: sol.sectors,
      productUrl: sol.productUrl,
    }));

  } catch (error) {
    console.error("Failed to fetch solutions:", error);
    return [];
  }
};

export const getSolutionById = async (id: string): Promise<Solution | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/solutions/${id}`);
    if (!response.ok) {
      if (response.status === 404) {
        console.warn(`Solution with ID '${id}' not found (404).`);
        return null; // Not found is a valid case
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    // Expect an object like { success: boolean, data: Solution }
    const responseData = await response.json();
    
    // Extract the solution from the data property
    const solution: Solution = responseData.data;

    // Check if data property actually contained a solution
    if (!solution) {
      console.error(`API response for ID '${id}' did not contain solution data:`, responseData);
      return null;
    }

    // Convert implementationStep icon names to components
    if (solution.implementationSteps) {
        solution.implementationSteps = solution.implementationSteps.map(step => ({
            ...step,
            icon: getImplementationIcon(step.iconName)
        }));
    }

    return solution;

  } catch (error) {
    console.error(`Failed to fetch solution with id ${id}:`, error);
    return null; // Return null on error
  }
};

export const getFilteredSolutions = async (
  tier?: SolutionTier | 'all',
  challenge?: Challenge | 'all',
  sector?: Sector | 'all'
): Promise<SolutionListItem[]> => {
  const allSolutionsList = await getAllSolutions();

  if (!allSolutionsList) {
      return [];
  }

  return allSolutionsList.filter(solution => {
    const tierMatch = !tier || tier === 'all' || solution.tier === tier;
    const challengeMatch = !challenge || challenge === 'all' || solution.challenges.includes(challenge);
    const sectorMatch = !sector || sector === 'all' || solution.sectors.includes(sector);
    return tierMatch && challengeMatch && sectorMatch;
  });
};

let cachedChallenges: Challenge[] | null = null;
let cachedSectors: Sector[] | null = null;

export const getAllChallenges = async (): Promise<Challenge[]> => {
  if (cachedChallenges) return cachedChallenges;
  
  const allSolutionsList = await getAllSolutions();
  if (!allSolutionsList) return [];

  const challengesSet = new Set<Challenge>();
  allSolutionsList.forEach(solution => {
    solution.challenges.forEach(challenge => challengesSet.add(challenge));
  });
  cachedChallenges = Array.from(challengesSet).sort();
  return cachedChallenges;
};

export const getAllSectors = async (): Promise<Sector[]> => {
  if (cachedSectors) return cachedSectors;

  const allSolutionsList = await getAllSolutions();
  if (!allSolutionsList) return [];

  const sectorsSet = new Set<Sector>();
  allSolutionsList.forEach(solution => {
    solution.sectors.forEach(sector => sectorsSet.add(sector));
  });
  cachedSectors = Array.from(sectorsSet).sort();
  return cachedSectors;
};

export const getAllTiers = (): SolutionTier[] => {
  return ['bronze', 'silver', 'gold'];
};

export default {
  getAllSolutions,
  getFilteredSolutions,
  getSolutionById,
  getAllChallenges,
  getAllSectors,
  getAllTiers,
};
